import React, { SetStateAction, useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Button,
  Tooltip,
  CircularProgress,
  DialogTitle,
  DialogContent,
  Dialog,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import AvailabilityHours from './AvailabilityHours';
import BlockedTime from './BlockedTime';
import AvailabilityCalendar from './AvailabilityCalendar';
import toggleToast from '../../../utils/messaging';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import { IAvailability, IBlockTime } from '@homeporter/types';
import { View } from 'react-big-calendar';
import { SaveOutlined } from '@mui/icons-material';
import { days } from '../../../global/interfaces';
import {
  useAvailabilitySettingsStyles,
  StyledOverlay,
  StyledModal,
  CustomTabs,
} from './styles';
import SaveIcon from '../../../icons/SaveIcon';
interface IProps {
  profile: any;
  isModalOpen: boolean;
  handleCloseModal: React.Dispatch<SetStateAction<boolean>>;
}

const AvailabilitySettings = ({ isModalOpen, handleCloseModal }: IProps) => {
  const styles = useAvailabilitySettingsStyles();
  const { profile, plans, services, userSettings, updateUserSettings } =
    useBookingsContext();
  const [navTabValue, setNavTabValue] = useState(0);

  const [loading, setLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [blockTimes, setBlockTimes] = useState<IBlockTime[]>([]);
  useEffect(() => {
    setBlockTimes(userSettings?.blockTime || []);
  }, [userSettings]);

  const [currentView, setCurrentView] = useState<View>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const [timezone, setTimezone] = useState(
    userSettings.timezone || 'GMT-05:00 - America/Toronto',
  );

  const [availability, setAvailability] = useState<IAvailability[]>(
    userSettings?.availability || [],
  );

  const [bookingLeadTime, setBookingLeadTime] = useState<number>(
    userSettings?.bookingLeadTime || 4,
  );
  const [bookingAvailabilityWindow, setBookingAvailabilityWindow] =
    useState<number>(userSettings?.bookingAvailabilityWindow || 14);

  const [selectedPlans, setSelectedPlans] = useState<number[]>(services);

  const onCloseModal = () => {
    handleCloseModal(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (showSave && newValue === 2) {
      setErrorMessage('Save changes to see on calendar.');
    }

    setNavTabValue(newValue);
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const hasBlankValues = availability.some(
        (item) => !item.day || item.from === '-:-' || item.to === '-:-',
      );

      if (hasBlankValues) {
        setErrorMessage(
          'Some values for day and time are blank. Please fill them in before saving.',
        );
        setLoading(false);
        return;
      }

      const sortedAvailability = [...availability].sort((a, b) => {
        const dayComparison = days.indexOf(a.day) - days.indexOf(b.day);
        if (dayComparison !== 0) return dayComparison;
        return a.from.localeCompare(b.from);
      });

      const sortedBlockTime = [...blockTimes];

      //confirm which values have changed from the initial user settings
      if (
        !(await updateUserSettings({
          settings: {
            availability: sortedAvailability,
            timezone,
            bookingLeadTime,
            bookingAvailabilityWindow,
            blockTime: sortedBlockTime,
          },
          services: selectedPlans,
        }))
      ) {
        throw new Error('Failed to update user settings');
      }

      await new Promise((resolve) => setTimeout(resolve, 250));
      setAvailability(sortedAvailability);
      setBlockTimes(sortedBlockTime);
      toggleToast('User settings updated successfully', 'success');
    } catch (err) {
      console.error('Failed to save blocked time', err);
    } finally {
      setLoading(false);
      setShowSave(false);
    }
  };

  return (
    <StyledOverlay open={isModalOpen} onClose={onCloseModal}>
      <StyledModal>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}>
          <Box>
            <CustomTabs
              value={navTabValue}
              onChange={handleTabChange}
              aria-label="Top Navigation">
              <Tab label="Hours of availability" value={0} />
              <Tab label="Blocked Time" value={1} />
              <Tab label="Calendar" value={2} />
            </CustomTabs>
          </Box>
          {showSave && (
            <Button
              sx={styles.saveButton}
              onClick={() => handleSave()}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SaveIcon />
              }>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
          {!showSave && (
            <Box>
              <Tooltip title="Copy booking URL">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    const bookingUrl = `${window._env_?.BOOKING_APP_URL || process.env.REACT_APP_BOOKING_APP_URL}/services/${profile?.resource_id}`;
                    navigator.clipboard.writeText(bookingUrl);
                    toggleToast('Booking URL copied to clipboard!', 'success');
                  }}
                  sx={{
                    borderRadius: '2rem',
                    textTransform: 'none',
                    height: '2.25rem',
                  }}>
                  Copy Booking URL
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
        {navTabValue === 0 && (
          <Box display={'block'} width={'100%'}>
            <AvailabilityHours
              plans={plans}
              services={services}
              setShowSave={setShowSave}
              selectedPlans={selectedPlans}
              setSelectedPlans={setSelectedPlans}
              availability={availability}
              setAvailability={setAvailability}
              bookingLeadTime={bookingLeadTime}
              setBookingLeadTime={setBookingLeadTime}
              bookingAvailabilityWindow={bookingAvailabilityWindow}
              setBookingAvailabilityWindow={setBookingAvailabilityWindow}
              timezone={timezone}
              setTimezone={setTimezone}
            />
          </Box>
        )}
        {navTabValue === 1 && (
          <BlockedTime
            setShowSave={setShowSave}
            blockTimes={blockTimes}
            setBlockTimes={setBlockTimes}
          />
        )}
        {navTabValue === 2 && (
          <AvailabilityCalendar
            showSave={showSave}
            currentView={currentView}
            setCurrentView={setCurrentView}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
          />
        )}
        <Dialog open={errorMessage !== ''} onClose={() => setErrorMessage('')}>
          <DialogTitle>Error</DialogTitle>
          <DialogContent>
            <DialogContentText>{errorMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorMessage('')} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </StyledModal>
    </StyledOverlay>
  );
};

export default AvailabilitySettings;
